import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AdminDataTable from "../../components/AdminDataTable";
import {
  AdminRowActionsMenu,
  buildActionsColumn,
  useAdminRowActionsMenu,
} from "../../components/AdminRowActions";
import { useNavigate } from "react-router-dom";
import {
  createAchievementCategory,
  fetchAchievementCategories,
  updateAchievementCategory,
} from "../../../lib/api";

const MAX_IMAGE_BYTES = 50 * 1024 * 1024;

function emptyAddForm() {
  return { name: "", description: "", mediaFile: null, previewUrl: "" };
}

function emptyEditForm() {
  return { name: "", description: "", mediaFile: null, previewUrl: "" };
}

function rowIsActive(row) {
  return row.active !== false;
}

const CATEGORY_COLUMNS = [
  { key: "sortOrder", label: "#", width: "64px", render: (r) => String(r.sortOrder ?? 0) },
  {
    key: "icon",
    label: "Icon",
    width: "72px",
    render: (r) => {
      const url = r.iconUrl;
      if (!url) return "—";
      return (
        <img
          src={url}
          alt=""
          className="adm-banner-thumb"
          style={{ width: 48, height: 48, objectFit: "contain" }}
        />
      );
    },
  },
  { key: "name", label: "Name", width: "18%", tdClassName: "adm-data-cell-name" },
  {
    key: "description",
    label: "Description",
    width: "24%",
    tdClassName: "adm-data-cell-muted",
    render: (r) => {
      const t = String(r.description ?? "").trim();
      if (!t) return "—";
      return t.length > 100 ? `${t.slice(0, 100)}…` : t;
    },
  },
  {
    key: "status",
    label: "Status",
    width: "100px",
    render: (r) => (
      <span className={`adm-status-pill${rowIsActive(r) ? " adm-status-pill--active" : " adm-status-pill--inactive"}`}>
        {rowIsActive(r) ? "Active" : "Inactive"}
      </span>
    ),
  },
];

export default function AdminAchievementCategoryMaster() {
  const navigate = useNavigate();
  const replaceFileInputRef = useRef(null);
  const addPreviewUrlRef = useRef("");
  const editPreviewUrlRef = useRef("");

  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [formError, setFormError] = useState("");
  const [imagePickError, setImagePickError] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm());
  const [drawerMode, setDrawerMode] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm());
  const rowActions = useAdminRowActionsMenu();

  const revokeAddPreview = useCallback(() => {
    if (addPreviewUrlRef.current) {
      URL.revokeObjectURL(addPreviewUrlRef.current);
      addPreviewUrlRef.current = "";
    }
  }, []);

  const revokeEditPreview = useCallback(() => {
    if (editPreviewUrlRef.current) {
      URL.revokeObjectURL(editPreviewUrlRef.current);
      editPreviewUrlRef.current = "";
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const isActiveParam = filter === "active" ? true : filter === "inactive" ? false : undefined;
      const [allRes, filteredRes] = await Promise.all([
        fetchAchievementCategories(),
        isActiveParam === undefined ? fetchAchievementCategories() : fetchAchievementCategories(isActiveParam),
      ]);
      setAllRecords(allRes.data || []);
      setRecords(isActiveParam === undefined ? allRes.data || [] : filteredRes.data || []);
    } catch (err) {
      setListError(err.message || "Could not load achievements.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(
    () => () => {
      revokeAddPreview();
      revokeEditPreview();
    },
    [revokeAddPreview, revokeEditPreview]
  );

  const onAddImageFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setImagePickError("Please choose an image file (PNG, JPG, WebP, etc.).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImagePickError("That file is too large.");
      return;
    }
    setImagePickError("");
    setFormError("");
    revokeAddPreview();
    const previewUrl = URL.createObjectURL(file);
    addPreviewUrlRef.current = previewUrl;
    setAddForm((f) => ({ ...f, mediaFile: file, previewUrl }));
  };

  const clearAddImage = () => {
    revokeAddPreview();
    setImagePickError("");
    setAddForm((f) => ({ ...f, mediaFile: null, previewUrl: "" }));
  };

  const onEditImageFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setImagePickError("Please choose an image file (PNG, JPG, WebP, etc.).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImagePickError("That file is too large.");
      return;
    }
    setImagePickError("");
    setFormError("");
    revokeEditPreview();
    const previewUrl = URL.createObjectURL(file);
    editPreviewUrlRef.current = previewUrl;
    setEditForm((f) => ({ ...f, mediaFile: file, previewUrl }));
  };

  const clearEditImage = () => {
    if (drawerMode !== "edit") return;
    revokeEditPreview();
    setImagePickError("");
    setEditForm((f) => ({ ...f, mediaFile: null, previewUrl: editingRow?.iconUrl ?? "" }));
  };

  const cancelAdd = useCallback(() => {
    revokeAddPreview();
    setAddOpen(false);
    setAddForm(emptyAddForm());
    setFormError("");
    setImagePickError("");
  }, [revokeAddPreview]);

  const closeDrawer = useCallback(() => {
    revokeEditPreview();
    setDrawerMode(null);
    setEditingRow(null);
    setEditForm(emptyEditForm());
    setFormError("");
    setImagePickError("");
    rowActions.setActionMenu(null);
  }, [revokeEditPreview, rowActions]);

  const openView = (row) => {
    revokeEditPreview();
    setEditingRow(row);
    setEditForm({
      name: row.name ?? "",
      description: row.description ?? "",
      mediaFile: null,
      previewUrl: row.iconUrl ?? "",
    });
    setImagePickError("");
    setDrawerMode("view");
  };

  const openUpdate = (row) => {
    revokeEditPreview();
    setEditingRow(row);
    setEditForm({
      name: row.name ?? "",
      description: row.description ?? "",
      mediaFile: null,
      previewUrl: row.iconUrl ?? "",
    });
    setImagePickError("");
    setFormError("");
    setDrawerMode("edit");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingRow) return;
    const name = String(editForm.name).trim();
    if (!name) return;

    const hasIcon = Boolean(editForm.mediaFile) || Boolean(editForm.previewUrl);
    if (!hasIcon) {
      setImagePickError("Please upload an achievement icon/image.");
      return;
    }

    setSaving(true);
    setFormError("");
    setImagePickError("");
    try {
      await updateAchievementCategory(editingRow.id, {
        name,
        description: String(editForm.description ?? "").trim(),
        file: editForm.mediaFile || undefined,
      });
      closeDrawer();
      await reload();
    } catch (err) {
      setFormError(err.message || "Could not save achievement.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!addOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") cancelAdd();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [addOpen, cancelAdd]);

  useEffect(() => {
    if (!drawerMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerMode, closeDrawer]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    const name = String(addForm.name).trim();
    if (!name) return;

    if (!addForm.mediaFile) {
      setImagePickError("Please upload an achievement icon/image.");
      return;
    }

    setSaving(true);
    setFormError("");
    setImagePickError("");
    try {
      await createAchievementCategory({
        name,
        description: String(addForm.description ?? "").trim(),
        file: addForm.mediaFile,
      });
      revokeAddPreview();
      setAddForm(emptyAddForm());
      setAddOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message || "Could not create achievement.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const setCategoryActive = async (row, active) => {
    try {
      await updateAchievementCategory(row.id, { active });
      await reload();
    } catch (err) {
      setListError(err.message || "Could not update status.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    }
  };

  const counts = useMemo(() => {
    const active = allRecords.filter((r) => rowIsActive(r)).length;
    return { all: allRecords.length, active, inactive: allRecords.length - active };
  }, [allRecords]);

  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const ao = Number(a.sortOrder) || 0;
      const bo = Number(b.sortOrder) || 0;
      if (ao !== bo) return ao - bo;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [records]);

  const tableColumns = useMemo(() => {
    const mapped = CATEGORY_COLUMNS.map((c) => ({
      id: c.key,
      header: c.label,
      width: c.width,
      tdClassName: c.tdClassName,
      render: (row) => (c.render ? c.render(row) : String(row[c.key] ?? "—")),
    }));
    return [
      ...mapped,
      buildActionsColumn({
        rowActions,
        getRowLabel: (row) => row.name || "achievement",
      }),
    ];
  }, [rowActions]);

  const openAdd = () => {
    revokeAddPreview();
    setAddForm(emptyAddForm());
    setFormError("");
    setImagePickError("");
    setAddOpen(true);
  };

  const renderImageField = ({ previewUrl, isView, isCreate, onFileChange, onClear, showRemove }) => (
    <div className="adm-field">
      <span className="adm-field-label">
        Achievement Icon/Image{!isView ? <span className="adm-req"> *</span> : null}
      </span>
      <p className="adm-upload-hint">PNG, JPG, or WebP. Used as the achievement icon on the website.</p>

      {previewUrl ? (
        <div className="adm-banner-picker-preview">
          <img src={previewUrl} alt="" className="adm-banner-picker-img" style={{ objectFit: "contain" }} />
          {!isView ? (
            <div className="adm-banner-picker-actions">
              <button
                type="button"
                className="adm-btn adm-btn--ghost adm-btn--sm"
                onClick={() => replaceFileInputRef.current?.click()}
              >
                Change file
              </button>
              <input
                ref={replaceFileInputRef}
                type="file"
                accept="image/*"
                className="adm-visually-hidden"
                onChange={onFileChange}
              />
              {showRemove ? (
                <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={onClear}>
                  Remove file
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : !isView ? (
        <label className="adm-file-picker">
          <input type="file" accept="image/*" onChange={onFileChange} />
          <span className="adm-file-picker-label">Choose image…</span>
          <span className="adm-file-picker-hint">Images only</span>
        </label>
      ) : (
        <p className="adm-upload-hint">No icon uploaded.</p>
      )}

      {imagePickError ? (
        <p className="adm-field-error" role="alert">
          {imagePickError}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="adm-page adm-page--wide">
      <div className="adm-embedded-simple-crud">
        <header className="adm-page-head adm-page-head--embedded adm-page-head--row">
          <h2 className="adm-page-title adm-page-title--embedded">Achievement Master</h2>
          <button type="button" className="adm-btn adm-btn--primary" onClick={openAdd} disabled={addOpen}>
            Add achievement
          </button>
        </header>

        {listError ? (
          <p className="adm-field-error" role="alert">
            {listError}
          </p>
        ) : null}

        <div className="adm-banner-toolbar adm-banner-toolbar--filters-only">
          <div className="adm-banner-toolbar-left" role="tablist" aria-label="Filter achievements by status">
            <button
              type="button"
              role="tab"
              aria-selected={filter === "all"}
              className={`adm-filter-tab${filter === "all" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All ({counts.all})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "active"}
              className={`adm-filter-tab${filter === "active" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("active")}
            >
              Active ({counts.active})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "inactive"}
              className={`adm-filter-tab${filter === "inactive" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("inactive")}
            >
              Inactive ({counts.inactive})
            </button>
          </div>
        </div>

        {addOpen &&
          createPortal(
            <div className="adm-drawer-root">
              <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={cancelAdd} />
              <aside
                className="adm-drawer-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="adm-achievement-master-drawer-title"
              >
                <div className="adm-drawer-panel-inner">
                  <header className="adm-drawer-header">
                    <h2 id="adm-achievement-master-drawer-title" className="adm-drawer-title">
                      Add achievement
                    </h2>
                    <button type="button" className="adm-drawer-close" onClick={cancelAdd} aria-label="Close">
                      <span aria-hidden>×</span>
                    </button>
                  </header>
                  <form className="adm-drawer-form" onSubmit={handleAddCategory}>
                    <div className="adm-drawer-fields">
                      <label className="adm-field">
                        <span className="adm-field-label">
                          Achievement Name<span className="adm-req"> *</span>
                        </span>
                        <input
                          className="adm-input"
                          value={addForm.name}
                          onChange={(e) => {
                            setAddForm((f) => ({ ...f, name: e.target.value }));
                            setFormError("");
                          }}
                          placeholder="e.g. Sports, Annual Day"
                          autoFocus
                          autoComplete="off"
                        />
                        <p className="adm-upload-hint">Achievement names must be unique (not case-sensitive).</p>
                      </label>

                      <label className="adm-field">
                        <span className="adm-field-label">Achievement Description</span>
                        <textarea
                          className="adm-input adm-input--textarea"
                          rows={4}
                          value={addForm.description}
                          onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                          placeholder="Short description of this achievement category"
                        />
                      </label>

                      {renderImageField({
                        previewUrl: addForm.previewUrl,
                        isView: false,
                        isCreate: true,
                        onFileChange: onAddImageFile,
                        onClear: clearAddImage,
                        showRemove: Boolean(addForm.previewUrl),
                      })}

                      {formError ? (
                        <p className="adm-field-error" role="alert">
                          {formError}
                        </p>
                      ) : null}
                    </div>
                    <footer className="adm-drawer-footer">
                      <button type="button" className="adm-btn adm-btn--ghost" onClick={cancelAdd}>
                        Cancel
                      </button>
                      <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </footer>
                  </form>
                </div>
              </aside>
            </div>,
            document.body
          )}

        {(drawerMode === "view" || drawerMode === "edit") &&
          createPortal(
            <div className="adm-drawer-root">
              <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={closeDrawer} />
              <aside className="adm-drawer-panel" role="dialog" aria-modal="true">
                <div className="adm-drawer-panel-inner">
                  <header className="adm-drawer-header">
                    <h2 className="adm-drawer-title">
                      {drawerMode === "view" ? "View achievement" : "Update achievement"}
                    </h2>
                    <button type="button" className="adm-drawer-close" onClick={closeDrawer} aria-label="Close">
                      <span aria-hidden>×</span>
                    </button>
                  </header>
                  <form className="adm-drawer-form" onSubmit={saveEdit}>
                    <div className="adm-drawer-fields">
                      <label className="adm-field">
                        <span className="adm-field-label">Achievement Name</span>
                        <input
                          className="adm-input"
                          value={editForm.name}
                          onChange={(e) => {
                            setEditForm((f) => ({ ...f, name: e.target.value }));
                            setFormError("");
                          }}
                          readOnly={drawerMode === "view"}
                          disabled={drawerMode === "view"}
                        />
                      </label>

                      <label className="adm-field">
                        <span className="adm-field-label">Achievement Description</span>
                        <textarea
                          className="adm-input adm-input--textarea"
                          rows={4}
                          value={editForm.description}
                          onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                          readOnly={drawerMode === "view"}
                          disabled={drawerMode === "view"}
                        />
                      </label>

                      {renderImageField({
                        previewUrl: editForm.previewUrl,
                        isView: drawerMode === "view",
                        isCreate: false,
                        onFileChange: onEditImageFile,
                        onClear: clearEditImage,
                        showRemove: drawerMode === "edit" && Boolean(editForm.mediaFile),
                      })}

                      {formError ? (
                        <p className="adm-field-error" role="alert">
                          {formError}
                        </p>
                      ) : null}
                    </div>
                    <footer className="adm-drawer-footer">
                      <button type="button" className="adm-btn adm-btn--ghost" onClick={closeDrawer}>
                        {drawerMode === "view" ? "Close" : "Cancel"}
                      </button>
                      {drawerMode === "edit" ? (
                        <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                          {saving ? "Saving…" : "Save changes"}
                        </button>
                      ) : null}
                    </footer>
                  </form>
                </div>
              </aside>
            </div>,
            document.body
          )}

        <AdminRowActionsMenu
          actionMenu={rowActions.actionMenu}
          onClose={rowActions.close}
          ariaLabel="Achievement actions"
          isActive={rowActions.actionMenu ? rowIsActive(rowActions.actionMenu.row) : false}
          onView={(row) => openView(row)}
          onUpdate={(row) => openUpdate(row)}
          onToggleActive={(row) => setCategoryActive(row, !rowIsActive(row))}
        />

        <section className="adm-card adm-card--table">
          {loading ? (
            <p className="adm-empty">Loading…</p>
          ) : (
            <AdminDataTable
              layout="fixed"
              columns={tableColumns}
              rows={sortedRecords}
              getRowKey={(r) => r.id}
              emptyState={
                allRecords.length === 0 ? (
                  <span className="adm-empty adm-empty--in-table">No achievements yet.</span>
                ) : (
                  <span className="adm-empty adm-empty--in-table">No achievements match this filter.</span>
                )
              }
            />
          )}
        </section>
      </div>
    </div>
  );
}
